"use client";

import { useEffect, useState } from "react";
import type { Background, BackgroundPage } from "@/lib/types/background";

interface Props {
  page: BackgroundPage;
  /** ユーザーが選択中の背景ID。指定時はデフォルト背景より優先される */
  overrideId?: string;
  /** 背景を読み終えたら、上に重ねた黒い膜の濃さ（0〜1）を知らせる。背景なしなら 0 */
  onOverlayChange?: (opacity: number) => void;
}

/**
 * 絵が画面より縦長で、画面いっぱいに敷くと絵のこれ未満しか見えないなら「全体を収める」出し方に切り替える。
 * 縦長の絵を横長の画面（パソコン）で敷くと、真ん中の3割ほどが引き伸ばされてしまうため。
 * 横長の絵を縦長の画面（スマホ）で見るときは、絵の高さを画面の高さに合わせて左右を切る（いつもの全面）
 */
const MIN_VISIBLE_FRACTION = 0.6;

/** 画面いっぱいに敷いたときに見える割合（1 = 全部見える） */
function visibleFraction(mediaRatio: number, screenRatio: number): number {
  return Math.min(mediaRatio / screenRatio, screenRatio / mediaRatio);
}

function useScreenRatio(): number | null {
  const [ratio, setRatio] = useState<number | null>(null);
  useEffect(() => {
    const update = () => setRatio(window.innerWidth / window.innerHeight);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return ratio;
}

export default function PageBackground({ page, overrideId, onOverlayChange }: Props) {
  /** undefined = まだ読んでいない / null = 背景なし */
  const [bg, setBg] = useState<Background | null | undefined>(undefined);
  /** 絵（動画）の縦横の比。読み込むまでは null */
  const [mediaRatio, setMediaRatio] = useState<{ url: string; ratio: number } | null>(null);
  const screenRatio = useScreenRatio();

  useEffect(() => {
    // "none" = 明示的に背景を外した状態 → 何も表示しない
    if (overrideId === "none") {
      setBg(null);
      return;
    }
    const url = overrideId
      ? `/api/backgrounds?backgroundId=${encodeURIComponent(overrideId)}`
      : `/api/backgrounds?page=${page}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Background | null) => setBg(data))
      .catch(() => setBg(null));
  }, [page, overrideId]);

  // 黒い膜は絵があるときだけ敷くので、絵がなければ 0 を知らせる。読み終える前は知らせない
  const overlayOpacity = bg === undefined ? null : bg?.imageUrl ? bg.overlayOpacity : 0;
  useEffect(() => {
    if (overlayOpacity !== null) onOverlayChange?.(overlayOpacity);
  }, [overlayOpacity, onOverlayChange]);

  // 絵の縦横の比を読む（動画は <video> の loadedmetadata で読む）
  useEffect(() => {
    if (!bg?.imageUrl || bg.mediaType === "video") return;
    const url = bg.imageUrl;
    const img = new Image();
    img.onload = () => {
      if (img.naturalHeight > 0) setMediaRatio({ url, ratio: img.naturalWidth / img.naturalHeight });
    };
    img.src = url;
    return () => {
      img.onload = null;
    };
  }, [bg?.imageUrl, bg?.mediaType]);

  if (!bg?.imageUrl) return null;

  const ratio = mediaRatio?.url === bg.imageUrl ? mediaRatio.ratio : null;
  // 比が分かるまでは、ぼかした下地だけを出す（引き伸ばされた絵が一瞬出ないように）
  const fit: "cover" | "contain" | null =
    ratio === null || screenRatio === null
      ? null
      : ratio < screenRatio && visibleFraction(ratio, screenRatio) < MIN_VISIBLE_FRACTION
        ? "contain"
        : "cover";

  return (
    <>
      {/* 背景メディア */}
      {bg.mediaType === 'video' ? (
        <>
          {fit === "contain" && <div className="fixed inset-0 -z-30 bg-black" aria-hidden="true" />}
          <video
            key={bg.imageUrl}
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              if (v.videoHeight > 0) setMediaRatio({ url: bg.imageUrl, ratio: v.videoWidth / v.videoHeight });
            }}
            className={`fixed inset-0 -z-20 h-full w-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
          >
            <source src={bg.imageUrl} />
          </video>
        </>
      ) : (
        <>
          {/* 下地: 同じ絵をぼかして敷く（全体を収めたときの余白を埋める） */}
          {fit !== "cover" && (
            <div
              className="fixed inset-0 -z-30 scale-110 bg-cover bg-center bg-no-repeat blur-2xl brightness-75"
              style={{ backgroundImage: `url(${bg.imageUrl})` }}
              aria-hidden="true"
            />
          )}
          {fit !== null && (
            <div
              className={`fixed inset-0 -z-20 bg-center bg-no-repeat ${fit === "contain" ? "bg-contain" : "bg-cover"}`}
              style={{ backgroundImage: `url(${bg.imageUrl})` }}
              aria-hidden="true"
            />
          )}
        </>
      )}
      {/* オーバーレイ（半透明の暗いシート） */}
      <div
        className="fixed inset-0 -z-10 bg-black"
        style={{ opacity: bg.overlayOpacity }}
        aria-hidden="true"
      />
      {/* ビネット: 上部・下部を暗くしてヘッダー・ナビの文字視認性を向上 */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, transparent 22%, transparent 72%, rgba(0,0,0,0.5) 100%)",
        }}
        aria-hidden="true"
      />
    </>
  );
}
